import { useEffect, useState, useCallback, useRef } from 'react';
import { useGetPrivateAudios, useGetPublicAudios } from '../audios';
import { useFetchRequiredCredits } from '../../services';
import { useAppContext } from '../context';
import { useDialog } from '../useDialog';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { usePopover } from '../text-to-speech';
import {
  AVATAR_DEFAULT_NAME_PREFIX,
  AvatarDataType,
  getRandomInt,
  isCreditsAvailable,
  MediaStatus,
  TalkingFaceModelType,
  VideoDetailsType,
} from '../../../t2c-fe/utils';
import { AudioCredit, Avatar } from '../../../t2c-fe/@types';
import { useCreateTalkingFaceVideo } from './useCreateTalkingFaceVideo';
import { useCreditContext } from '../useCreditContext';
import { useEnhancePrompt } from '../broll/useEnchancePrompt';

export const useTalkingFace = ({ refetchTalkingFaces }) => {
  // Fetch audio data
  const { data: privateAudioData } = useGetPrivateAudios({
    filters: { status: MediaStatus.Success },
  });
  const { data: publicAudioData } = useGetPublicAudios();

  // State management
  const [talkingFaceData, setTalkingFaceData] = useState({
    text: '',
    name: AVATAR_DEFAULT_NAME_PREFIX + getRandomInt(1, 1000),
    avatarId: null,
    audioId: null, // Will be set when data is available
  });
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarDataType | Avatar | null>(null);
  const [selectedAvatarImage, setSelectedAvatarImage] = useState<string | null>(null);
  const [newGenerationData, setNewGenerationData] = useState<VideoDetailsType | undefined>();
  const [creditsData, setCreditsData] = useState<AudioCredit | null>(null);
  const [isTrain, setIsTrain] = useState(false);
  const [enableMotion, setEnableMotion] = useState(true);
  const [modelName, setModelName] = useState<TalkingFaceModelType>(TalkingFaceModelType.HEDRA);

  // Get various context and hook values
  const {
    showAiGenModel: isProccessingModalOpen,
    handleOpenAiGenModal: openProccessingModal,
    handleCloseAiGenModal: closeProccessingModal,
    showCreditDialog,
  } = useAppContext();

  const { setUsableCredits } = useCreditContext();
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useRouter();
  const { popoverAnchor, handleOpenPopover, handleClosePopover } = usePopover();

  // Set up dialog hooks
  const {
    closeDialog: closeCreditDialog,
    isDialogOpen: isCreditDialogOpen,
    openDialog: openCreditDialog,
  } = useDialog();

  const {
    closeDialog: closeSelectAvatarDialog,
    isDialogOpen: isSelectAvatarDialog,
    openDialog: openSelectAvatarDialog,
  } = useDialog();

  // Set up API mutations - ensure they're only created once
  const {
    mutateAsync: mutateFetchRequiredCredits,
    isLoading: isFetchingRequiredCredits,
    data: requiredCreditsData,
    // No need to track error here as we handle errors in the try/catch
  } = useFetchRequiredCredits();

  // Create the video mutation only once
  const { mutateAsync, isLoading } = useCreateTalkingFaceVideo();

  const { mutateAsync: mutateEnhancePrompt, isLoading: isEnhancing } = useEnhancePrompt();

  // Set default audio when data becomes available
  useEffect(() => {
    if (
      !talkingFaceData.audioId &&
      (publicAudioData?.length || privateAudioData?.pages?.[0]?.data?.length)
    ) {
      setTalkingFaceData((prev) => ({
        ...prev,
        audioId: publicAudioData?.[0]?.id ?? privateAudioData?.pages?.[0]?.data?.[0]?.id ?? null,
      }));
    }
  }, [publicAudioData, privateAudioData, talkingFaceData.audioId]);

  useEffect(() => {
    if (modelName === TalkingFaceModelType.HEYGEN && selectedAvatar) {
      if (selectedAvatar?.metaData?.heyGenMotionId || !enableMotion) {
        setIsTrain(false);
      } else {
        setIsTrain(true);
      }
    } else {
      setIsTrain(false);
    }
  }, [modelName, selectedAvatar, enableMotion]);

  // Handler functions (memoized to prevent unnecessary re-renders)
  const handleDataChange = useCallback((key: string, value: string) => {
    setTalkingFaceData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAvatarSelect = useCallback(
    (data: AvatarDataType | Avatar) => {
      setTalkingFaceData((prev) => ({ ...prev, avatarId: data?.id }));
      setSelectedAvatarImage(data?.thumbnail || null);
      setSelectedAvatar(data);
      closeSelectAvatarDialog();
    },
    [closeSelectAvatarDialog]
  );

  const handleAudioSelect = useCallback((id: string) => {
    setTalkingFaceData((prev) => ({ ...prev, audioId: id }));
  }, []);

  const handleUpgrade = useCallback(() => {
    closeCreditDialog();
    push('/subscription');
  }, [push, closeCreditDialog]);

  const handleConfirmGeneration = useCallback(async () => {
    // Close the dialog first
    closeCreditDialog();

    try {
      const sanitizedData = {
        ...talkingFaceData,
        name: talkingFaceData.name.trim(),
        text: talkingFaceData.text.trim(),
        processedBackgroundId: talkingFaceData.avatarId,
        modelName: modelName,
        enableMotion: enableMotion,
      };

      // Add additional delay before API call to ensure any race conditions are resolved
      await new Promise((resolve) => setTimeout(resolve, 100));

      const res = await mutateAsync(sanitizedData);

      setNewGenerationData(res);

      if (creditsData?.requiredCredits) {
        setUsableCredits((prev) => prev - creditsData.requiredCredits);
      }

      openProccessingModal();
      refetchTalkingFaces();
    } catch (error) {
      enqueueSnackbar('Failed to generate', { variant: 'error' });
    }
  }, [
    talkingFaceData,
    mutateAsync,
    modelName,
    enableMotion,
    creditsData,
    setUsableCredits,
    openProccessingModal,
    refetchTalkingFaces,
    closeCreditDialog,
    enqueueSnackbar,
  ]);

  const handleCreateClick = useCallback(async () => {
    // Validate inputs
    let errMsg = '';
    if (!talkingFaceData.avatarId) errMsg = 'Please Select An Avatar.';
    if (!talkingFaceData.audioId) errMsg = 'Please Select An Audio.';
    if (!talkingFaceData.name.trim()) errMsg = 'Please Enter The Name.';
    if (!talkingFaceData.text.trim()) errMsg = 'Please Enter A script.';

    if (errMsg) {
      enqueueSnackbar(errMsg, { variant: 'error' });
      return;
    }

    try {
      const res = await mutateFetchRequiredCredits({
        text: talkingFaceData.text,
        model: modelName,
        isTrain: isTrain,
      });

      // Set the credits data after successful fetch - this will trigger the useEffect
      setCreditsData(res);
    } catch (error) {
      // On error, we need to reset the processed flag to allow retrying
      enqueueSnackbar('Failed to fetch credit requirements', { variant: 'error' });
    }
  }, [
    talkingFaceData,
    enqueueSnackbar,
    mutateFetchRequiredCredits,
    isTrain,
    modelName,
    enableMotion,
  ]);

  const handlePromptEnhance = useCallback(async () => {
    try {
      const res = await mutateEnhancePrompt({
        prompt: talkingFaceData.text,
        model: 'voiceover',
      });
      handleDataChange('text', res);
    } catch (error) {
      enqueueSnackbar('Failed to enhance prompt', { variant: 'error' });
    }
  }, [talkingFaceData.text, mutateEnhancePrompt, handleDataChange, enqueueSnackbar]);

  // Reset the completed flag when we initiate a new request
  useEffect(() => {
    if (!talkingFaceData.text || !talkingFaceData.audioId || !talkingFaceData.avatarId) {
      return;
    }
  }, [talkingFaceData.text, talkingFaceData.audioId, talkingFaceData.avatarId]);

  // Effect to handle credits data changes
  useEffect(() => {
    // Only proceed if we have credit data, aren't loading, and haven't processed this data yet
    if (!creditsData || isFetchingRequiredCredits) {
      return;
    }

    const isEnoughCredit = isCreditsAvailable(
      creditsData?.availableCredits,
      creditsData?.creditsInUse,
      creditsData?.requiredCredits
    );

    if (showCreditDialog) {
      // Only open the dialog, let the dialog handle the confirm action
      openCreditDialog();
    } else {
      if (isEnoughCredit) {
        // When showCreditDialog is false and we have enough credits,
        // only proceed with generation if we're not already generating
        handleConfirmGeneration();
      } else {
        // handleUpgrade();
        openCreditDialog();
      }
    }
  }, [creditsData, isFetchingRequiredCredits]);

  return {
    talkingFaceData,
    handleDataChange,
    handleCreateClick,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    isSelectAvatarDialog,
    openSelectAvatarDialog,
    closeSelectAvatarDialog,
    handleConfirmGeneration,
    privateAudioData,
    publicAudioData,
    popoverAnchor,
    handleOpenPopover,
    handleClosePopover,
    handleAvatarSelect,
    handleAudioSelect,
    selectedAvatarImage,
    isLoading,
    newGenerationData,
    isEnhancing,
    handlePromptEnhance,
    isTrain,
    modelName,
    setModelName,
    enableMotion,
    setEnableMotion,
  };
};
